/**
 * Luvè variant swap: keeps a block rendered in Liquid in step with Horizon's colour and size picker.
 *
 * When the shopper picks a variant, Horizon fetches the product section for it and refreshes its own price, gallery
 * and buy buttons from that response, but no other block. Wrap the variant-dependent part of a block in
 * <luve-variant-swap data-swap-id="{{ block.id }}"> and it is refreshed from the same response: the title's colour,
 * the free shipping note, the shipping table's highlighted row, the Corner Trio's colour.
 */
import { StandardEvents } from '@shopify/events';
import { morph } from '@theme/morph';

class LuveVariantSwap extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abortController;

  connectedCallback() {
    this.#abortController = new AbortController();
    document.addEventListener(StandardEvents.productSelect, this.#onProductSelect, {
      capture: true,
      signal: this.#abortController.signal,
    });
  }

  disconnectedCallback() {
    this.#abortController?.abort();
  }

  /** @param {Event & { promise?: Promise<any> }} event */
  #onProductSelect = (event) => {
    const { target } = event;
    // Product cards have pickers of their own; only the product this block belongs to counts
    if (!(target instanceof Element) || target.closest('product-card')) return;

    const scope = this.closest('.shopify-section, dialog');
    if (!scope || target.closest('.shopify-section, dialog') !== scope) return;

    event.promise
      ?.then((result) => {
        /** @type {Document | undefined} */
        const html = result?.detail?.html;
        const id = this.dataset.swapId;
        if (!html?.querySelector || !id || !this.isConnected) return;

        const incoming = html.querySelector(`luve-variant-swap[data-swap-id="${CSS.escape(id)}"]`);
        if (!incoming) return;
        morph(this, incoming);

        // The fresh markup carries the server's wording ("in 2-3 weeks"): date it again in the browser
        for (const promise of this.querySelectorAll('luve-delivery-promise')) {
          // @ts-ignore assets/luve-delivery-promise.js
          if (typeof promise.update === 'function') promise.update();
        }
      })
      // A selection overtaken by the next one is aborted; the next one brings this block up to date
      .catch(() => {});
  };
}

if (!customElements.get('luve-variant-swap')) {
  customElements.define('luve-variant-swap', LuveVariantSwap);
}
